import { ArrowLeft, Calendar, Clock, Share2 } from 'lucide-react';
import { marked } from 'marked';
import { useEffect } from 'react';
import { updatePageMeta, addStructuredData, generateBlogPostStructuredData } from '../../utils/seo';

marked.setOptions({
  breaks: true,
  gfm: true,
  headerIds: true,
  mangle: false,
  sanitize: false,
});

const post = {
  "slug": "lunar-new-year-gift-traditions",
  "title": "Lunar New Year Gift-Giving: Red Envelopes and Beyond",
  "excerpt": "Navigate Lunar New Year gift traditions with confidence. Learn about red envelopes, auspicious gifts, and cultural considerations.",
  "date": "2025-01-20",
  "readTime": "8 min read",
  "category": "Festivals",
  "image": "🧧",
  "heroImage": "https://images.unsplash.com/photo-1550995694-3f5d60b6e3e7?w=1200&h=600&fit=crop",
  "author": "SwapGifts Editorial Team",
  "content": "# Lunar New Year Gift-Giving: Red Envelopes and Beyond\n\nLunar New Year (also called Chinese New Year or Spring Festival) is celebrated across Asia with unique gift-giving customs steeped in symbolism and tradition.\n\n## The Red Envelope (Hongbao/Ang Pao)\n\nThe most iconic Lunar New Year gift is the red envelope containing money.\n\n**Traditional Rules**:\n- Always use new, crisp bills\n- Give even amounts (except at funerals)\n- Avoid the number 4 (sounds like \"death\" in Chinese)\n- Favor numbers 6, 8, and 9 (lucky numbers)\n- Married people give to unmarried people and children\n- Employees give to elders and parents\n\n**Typical Amounts**:\n- Children: $5-$20\n- Teenagers: $20-$50\n- Employees (from employer): $50-$200\n- Parents: $200-$500+\n\n## Traditional Gifts\n\n**Fruit Baskets**: Oranges and tangerines symbolize gold and good fortune. Always give in even numbers.\n\n**Tea**: Premium tea sets show respect and sophistication.\n\n**Alcohol**: High-quality liquor or wine for hosts and business associates.\n\n**Specialty Foods**: Dried seafood, premium nuts, or festive snacks.\n\n## Modern Gift Ideas\n\n**For Families**:\n- Luxury food hampers\n- Kitchen appliances\n- Home decor with auspicious symbols\n- Health supplements\n\n**For Friends**:\n- Skincare or wellness products\n- Books\n- Tech gadgets\n- Gift cards\n\n**For Children**:\n- Toys or games\n- Books\n- Educational gifts\n- Art supplies\n\n## What to Avoid\n\n**Unlucky Gifts**:\n- Clocks (symbolize \"time running out\")\n- Shoes (suggest walking away)\n- Umbrellas (sound like \"separation\" in Chinese)\n- Sharp objects (cutting relationships)\n- White or black items (funeral colors)\n- Pears (sound like \"separation\" in Chinese)\n- Handkerchiefs (associated with tears)\n\n## Gift-Wrapping Guidelines\n\n**Do**:\n- Use red, gold, or pink paper (lucky colors)\n- Add traditional decorations like tassels or Chinese knots\n- Include greeting cards with gold characters\n\n**Don't**:\n- Use white, black, or blue wrapping (unlucky colors)\n- Wrap in groups of four\n\n## Organizing a Lunar New Year Gift Exchange\n\nFor modern celebrations with friends or colleagues:\n\n**Lucky Number Theme**: Set budget at $28, $38, $58, or $88 (all contain lucky numbers)\n\n**Color-Coded Exchange**: Everyone brings a gift wrapped in red and gold, then draw numbers for selection order\n\n**Zodiac Theme**: Exchange gifts related to the year's zodiac animal\n\n## Timing\n\nGifts can be given throughout the 15-day celebration:\n- New Year's Eve: To family members\n- New Year's Day: Red envelopes to children\n- First few days: Visiting relatives and friends\n- 15th day (Lantern Festival): Concluding exchanges\n\n## Conclusion\n\nLunar New Year gift-giving is rich with meaning and tradition. By respecting cultural customs while adding personal touches, you bring joy and good fortune to loved ones."
};

export default function LunarNewYearGiftTraditionsPage() {
  useEffect(() => {
    updatePageMeta({
      title: `${post.title} | SwapGifts Blog`,
      description: post.excerpt,
      ogTitle: post.title,
      ogDescription: post.excerpt,
      ogImage: post.heroImage || 'https://swapgifts.store/default-blog-image.jpg',
      canonicalUrl: `https://swapgifts.store/blog/${post.slug}`,
      keywords: `gift exchange, ${post.category.toLowerCase()}, secret santa, ${post.title.toLowerCase()}`
    });

    addStructuredData(generateBlogPostStructuredData({
      title: post.title,
      excerpt: post.excerpt,
      date: post.date,
      author: post.author || 'SwapGifts Editorial Team',
      heroImage: post.heroImage,
      slug: post.slug
    }));
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <a
          href="/blog"
          className="inline-flex items-center gap-2 text-green-600 font-medium hover:underline mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Blog
        </a>

        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {post.heroImage && (
            <div className="w-full h-96 overflow-hidden">
              <img
                src={post.heroImage}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-8 md:p-12">
            <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                {post.category}
              </span>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {post.readTime}
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(post.date).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              {post.title}
            </h1>

            {post.author && (
              <p className="text-sm text-gray-500 mb-6">
                By <span className="font-medium text-gray-700">{post.author}</span>
              </p>
            )}

            <p className="text-xl text-gray-600 mb-8 leading-relaxed border-b border-gray-200 pb-8">
              {post.excerpt}
            </p>

            <div className="flex items-center gap-4 pb-8 mb-8 border-b border-gray-200">
              <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                <Share2 className="w-4 h-4" />
                Share
              </button>
            </div>

            <div
              className="prose prose-lg max-w-none
                prose-headings:font-bold prose-headings:text-gray-900 prose-headings:mt-8 prose-headings:mb-4
                prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6
                prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
                prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6
                prose-a:text-green-600 prose-a:no-underline hover:prose-a:underline
                prose-strong:text-gray-900 prose-strong:font-semibold
                prose-ul:my-6 prose-ul:space-y-2
                prose-ol:my-6 prose-ol:space-y-2
                prose-li:text-gray-700 prose-li:leading-relaxed
                prose-code:bg-gray-100 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-sm"
              dangerouslySetInnerHTML={{ __html: marked.parse(post.content) }}
            />
          </div>

          <div className="mt-12 bg-gradient-to-r from-green-600 to-blue-600 rounded-lg p-8 text-white text-center">
            <h2 className="text-2xl font-bold mb-4">Ready to organize your gift exchange?</h2>
            <p className="text-green-100 mb-6">
              Put these tips into action and create your exchange in minutes
            </p>
            <a
              href="/create"
              className="inline-block px-8 py-3 bg-white text-green-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              Start Drawing Names
            </a>
          </div>
        </div>
      </article>
    </div>
  );
}
